import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Phone, ArrowLeft } from 'lucide-react';
import { config } from '@workspace/public-utils'
import toast from 'react-hot-toast';
import { Seo } from '../../components/Seo';

const ForgotPasswordPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    mobile_number: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/v1/auth/password/reset-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSuccess(true);
        toast.success('임시 비밀번호가 SMS로 전송되었습니다');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/auth/login');
        }, 3000);
      } else {
        const error = await response.json();
        toast.error(error.detail || '비밀번호 재설정에 실패했습니다');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('비밀번호 재설정 중 오류가 발생했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Seo title="비밀번호 찾기" canonicalPath="/auth/forgot-password" noindex />
      <div className="max-w-md w-full">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-black gradient-text-happiness mb-2">
            비밀번호 찾기
          </h1>
          <p className="text-gray-600">
            가입 시 등록한 정보를 입력해주세요
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
        >
          {!isSuccess ? (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  계정 확인
                </h2>
                <p className="text-gray-600 text-sm">
                  사용자명과 휴대폰 번호를 입력하시면 임시 비밀번호를 SMS로 전송해드립니다
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                    사용자명
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:border-transparent transition-colors"
                      placeholder="사용자명을 입력하세요"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="mobile_number" className="block text-sm font-medium text-gray-700 mb-2">
                    휴대폰 번호
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="tel"
                      id="mobile_number"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:border-transparent transition-colors"
                      placeholder="010-1234-5678"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    가입 시 등록한 휴대폰 번호를 입력해주세요
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? '전송 중...' : '임시 비밀번호 받기'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="mb-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                전송 완료!
              </h3>
              <p className="text-gray-600 mb-4">
                입력하신 휴대폰 번호로 임시 비밀번호가 전송되었습니다.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                임시 비밀번호로 로그인 후 반드시 비밀번호를 변경해주세요.
              </p>
              <Link
                to="/auth/login"
                className="btn-primary inline-block"
              >
                로그인하러 가기
              </Link>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/auth/login"
              className="inline-flex items-center text-sm text-gray-600 hover:text-happiness-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              로그인으로 돌아가기
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-6"
        >
          <p className="text-sm text-gray-600">
            계정이 없으신가요?{' '}
            <Link
              to="/auth/register"
              className="text-happiness-primary font-semibold hover:underline"
            >
              회원가입
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
